/**
 * @file 权限原子类型定义
 */

/** 定义所有可能的资源模块 */
type Resource =
    | 'project' // 项目
    | 'member' // 成员管理
    // | 'finance'         // 财务管理（目前功能还未开发，暂时注释）
    | 'script' // 剧本
    | 'style' // 艺术定调
    | 'asset' // 主体资产（角色/场景/道具）
    | 'episode' // 剧集
    | 'storyboard' // 分镜
    | 'canvas' // 画布
    | 'media' // 媒体资产(文本、图片、视频、音频等)
    | 'editor'; // 编辑器

/** 定义所有可能的原子操作 */
type Action =
    | 'view' // 查看
    | 'view_progress' // 查看进度
    | 'create' // 创建
    | 'extract' // 解析(智能提取)
    | 'edit' // 编辑
    | 'edit_download' // 编辑下载权限
    | 'delete' // 删除
    | 'submit' // 提交
    | 'review' // 审核
    | 'annotate' // 批注
    | 'upload' // 上传
    | 'generate' // AI内容生产（文本、图片、视频、音频等）
    | 'import' // 导入
    | 'export' // 导出/下载
    | 'archive' // 归档
    | 'view_others' // 查看他人的
    | 'download' // 下载
    | 'confirm' // 确认
    | 'reject' // 驳回
    | 'copy' // 复制
    | 'save' // 保存
    | 'enter' // 进入
    | 'manage' // 管理（新增/复制/拖动/删除等）
    | 'ref_edit' // 编辑
    | 'ref_upload' // 上传
    | 'to_clip' // 剪辑
    | 'approve'; // 移动

/**
 * 定义权限原子类型
 * 使用模板字面量强制约束格式，自动生成 PermissionCode 类型：例如 "script:edit" | "script:view | script:*" ...
 */
export type PermissionCode = `${Resource}:${Action}`;

/**
 * 资源名大写映射（用于构建常量 key）
 */
type ResourceKey = Uppercase<Resource>;

/**
 * 权限常量字典类型约束
 * - 外层 key 为资源名大写
 * - 内层 value 必须是 PermissionCode 类型
 */
type PermissionMap = {
    [K in ResourceKey]: Record<string, PermissionCode>;
};

/**
 * 定义权限常量字典
 * @description 构建权限原子具名常量对象（增强可读性）
 * @remarks 使用 satisfies 操作符，既保留类型约束，又保持 IDE 提示
 * @example PERMISSIONS.SCRIPT.EDIT
 */
export const PERMISSIONS = {
    PROJECT: {
        /** 项目查看 */
        VIEW: 'project:view',
        /** 项目进度查看 */
        VIEW_PROGRESS: 'project:view_progress',
        /** 项目编辑 */
        EDIT: 'project:edit',
        /** 项目下载权限编辑 */
        EDIT_DOWNLOAD: 'project:edit_download',
        /** 项目删除 */
        DELETE: 'project:delete',
    },
    MEMBER: {
        /** 成员查看 */
        VIEW: 'member:view',
        /** 成员编辑 */
        EDIT: 'member:edit',
        /** 成员删除 */
        DELETE: 'member:delete',
    },
    // FINANCE: {
    //     /** 财务查看 */
    //     VIEW: 'finance:view',
    //     /** 财务编辑 */
    //     EDIT: 'finance:edit',
    //     /** 财务删除 */
    //     DELETE: 'finance:delete',
    // },
    SCRIPT: {
        /** 剧本上传 */
        UPLOAD: 'script:upload',
        /** 剧本查看 */
        VIEW: 'script:view',
        /** 剧本编辑 */
        EDIT: 'script:edit',
        /** 剧本提交 */
        SUBMIT: 'script:submit',
        /** 剧本确认 */
        CONFIRM: 'script:confirm',
        /** 剧本驳回 */
        REJECT: 'script:reject',
        /** 剧本审核 */
        REVIEW: 'script:review',
        /** 剧本删除 */
        DELETE: 'script:delete',
        /** 剧本下载 */
        DOWNLOAD: 'script:download',
        /** 剧本保存 */
        SAVE: 'script:save',
    },
    STYLE: {
        /** 艺术定调查看 */
        VIEW: 'style:view',
        /** 艺术定调编辑 */
        EDIT: 'style:edit',
        /** 艺术定调删除 */
        DELETE: 'style:delete',
    },
    ASSET: {
        /** 主体资产创建 */
        CREATE: 'asset:create',
        /** 主体资产解析 */
        EXTRACT: 'asset:extract',
        /** 主体资产查看 */
        VIEW: 'asset:view',
        /** 主体资产编辑 */
        EDIT: 'asset:edit',
        /** 主体资产AI生成图片 */
        GENERATE: 'asset:generate',
        /** 主体资产删除 */
        DELETE: 'asset:delete',
        /** 主体资产审核 */
        REVIEW: 'asset:review',
    },
    EPISODE: {
        /** 剧集创建 */
        CREATE: 'episode:create',
        /** 剧集查看 */
        VIEW: 'episode:view',
        /** 剧集编辑 */
        EDIT: 'episode:edit',
        /** 剧集审核 */
        REVIEW: 'episode:review',
        /** 剧集删除 */
        DELETE: 'episode:delete',
        /** 剧集管理：重命名、删除、恢复、永久删除 */
        MANAGE: 'episode:manage',
    },
    STORYBOARD: {
        /** 分镜解析 */
        EXTRACT: 'storyboard:extract',
        /** 分镜创建 */
        CREATE: 'storyboard:create',
        /** 分镜查看 */
        VIEW: 'storyboard:view',
        /** 分镜编辑 */
        EDIT: 'storyboard:edit',
        /** 分镜制作页字段编辑（时长、景别、画面描述、台词、角色动作、光影氛围、声音、画面提示词、视频提示词） */
        REF_EDIT: 'storyboard:ref_edit',
        /** 分镜制作页参考图/参考视频上传 */
        REF_UPLOAD: 'storyboard:ref_upload',
        /** 分镜制作页「去剪辑」入口展示 */
        TO_CLIP: 'storyboard:to_clip',
        /** 分镜AI生产图片/视频/音频 */
        GENERATE: 'storyboard:generate',
        /** 分镜审核 */
        APPROVE: 'storyboard:approve',
        /** 分镜素材批注 */
        ANNOTATE: 'storyboard:annotate',
        /** 分镜删除 */
        DELETE: 'storyboard:delete',
        /** 新增/复制/拖动/删除分镜 */
        MANAGE: 'storyboard:manage',
    },
    CANVAS: {
        /** 画布查看 */
        VIEW: 'canvas:view',
        /** 画布内容生产 */
        GENERATE: 'canvas:generate',
        /** 画布查看他人 */
        VIEW_OTHERS: 'canvas:view_others',
    },
    MEDIA: {
        /** 媒体资产（主体资产、分镜、剧集等媒体资源）下载导出 */
        EXPORT: 'media:export',
    },
    EDITOR: {
        /** 进入编辑器 */
        ENTER: 'editor:enter',
    },
} satisfies PermissionMap;
